import { NavLink } from "react-router-dom";
import type { ReactNode } from "react";
import { EnvSwitcher } from "./EnvSwitcher";
import { UserMenu } from "./UserMenu";
import { useCanvasEnv } from "../lib/envContext";
import { useRole } from "../lib/roleContext";

// Nav lists VIEWS only — the three pipeline histories in pipeline order.
// Actions live as buttons on the page they produce rows for ("New sync" on
// Syncs, "Test submission" on Canvas submissions), not as nav items, so
// looking and doing never read as siblings.
const nav = [
  { to: "/", label: "Dashboard", end: true },
  { section: "Pipeline" },
  { to: "/syncs", label: "Syncs" },
  { to: "/uploads", label: "Canvas submissions" },
  { to: "/sis-bundles", label: "SIS bundles" },
  { section: "System", adminOnly: true },
  { to: "/settings", label: "Settings", adminOnly: true },
] as const;

// Which AWS environment this bundle was BUILT for (dev sandbox vs prod).
// Inlined at build time: CI passes the workflow's env input, local builds
// read .env.development/.env.production. `||` (not ??) so an empty string
// from a misconfigured build still falls back.
const DEPLOY_ENV = import.meta.env.VITE_DEPLOY_ENV || "local";

export function Layout({ children }: { children: ReactNode }) {
  const { env } = useCanvasEnv();
  const { isAdmin } = useRole();
  // Read-only users never see admin destinations (the routes are guarded
  // too — RequireAdmin in App.tsx; the API is the real boundary).
  const visibleNav = nav.filter(
    (item) => isAdmin || !("adminOnly" in item && item.adminOnly),
  );
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-title">Canvas Sync</div>
          <div className="brand-subtitle">PeopleSoft &middot; Canvas</div>
        </div>
        <nav className="nav">
          {visibleNav.map((item) =>
            "section" in item ? (
              <div key={item.section} className="nav-section">
                {item.section}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={"end" in item ? item.end : false}
                className={({ isActive }) =>
                  "nav-item" + (isActive ? " nav-item-active" : "")
                }
              >
                {item.label}
              </NavLink>
            ),
          )}
        </nav>
        <div className="sidebar-foot" title={`AWS environment: ${DEPLOY_ENV}`}>
          <div className="foot-label">AWS env</div>
          <div className="foot-value">{DEPLOY_ENV}</div>
        </div>
      </aside>
      <div className="content">
        <header className="topbar">
          {env === "prod" && (
            <div className="prod-banner">
              <span className="prod-banner-dot" />
              Production mode · changes here affect real Canvas data
            </div>
          )}
          <div className="topbar-spacer" />
          <div className="topbar-right">
            <span className="topbar-label">Canvas</span>
            <EnvSwitcher />
            <UserMenu />
          </div>
        </header>
        <main className="main">{children}</main>
      </div>
    </div>
  );
}
